import {
  effect,
  exhaustMapWithTrailing,
  fromPromise,
  LiveData,
  onComplete,
  OnEvent,
  onStart,
  Service,
  smartRetry,
} from '@toeverything/infra';
import { catchError, EMPTY, tap } from 'rxjs';

import type { GlobalCache } from '../../storage';
import { AccountChanged } from '../events/account-changed';
import type {
  UpdateUserSettingsInput,
  UserSettings,
  UserSettingsStore,
} from '../stores/user-settings';

export type { UserSettings };

@OnEvent(AccountChanged, e => e.onAccountChanged)
export class UserSettingsService extends Service {
  constructor(
    private readonly store: UserSettingsStore,
    private readonly cache: GlobalCache
  ) {
    super();
  }

  userSettings$ = new LiveData<UserSettings | undefined>(undefined);
  isLoading$ = new LiveData<boolean>(false);
  error$ = new LiveData<any | undefined>(undefined);

  revalidate = effect(
    exhaustMapWithTrailing(() => {
      return fromPromise(() => {
        return this.store.getUserSettings();
      }).pipe(
        smartRetry(),
        tap(settings => {
          this.userSettings$.value = settings;
          // Sync server-side language to frontend i18n if no local preference is set
          this.syncLanguageFromServer(settings);
        }),
        catchError(error => {
          this.error$.value = error;
          return EMPTY;
        }),
        onStart(() => {
          this.isLoading$.value = true;
        }),
        onComplete(() => {
          this.isLoading$.value = false;
        })
      );
    })
  );

  private syncLanguageFromServer(settings?: UserSettings) {
    if (!settings?.language) return;
    // Track the last server-synced locale to detect Discourse-side changes
    const lastServerLocale = this.cache.get<string>('i18n_lng_server');
    const userChoseLanguage = this.cache.get<boolean>('i18n_lng_user_chosen');

    // Always update the server locale cache
    this.cache.set('i18n_lng_server', settings.language);

    // Apply server locale if:
    // 1. User hasn't explicitly chosen a language in AFFiNE, OR
    // 2. The server locale changed since last sync (user changed it in Discourse)
    if (!userChoseLanguage || settings.language !== lastServerLocale) {
      this.cache.set('i18n_lng', settings.language);
      // If server locale changed, clear the user-chosen flag since Discourse takes precedence
      if (settings.language !== lastServerLocale) {
        this.cache.set('i18n_lng_user_chosen', false);
      }
    }
  }

  private onAccountChanged() {
    this.revalidate();
  }

  async updateUserSettings(settings: UpdateUserSettingsInput) {
    await this.store.updateUserSettings(settings);
    this.userSettings$.value = {
      ...this.userSettings$.value,
      ...(settings as UserSettings),
    };
    this.revalidate();
  }
}
